import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Inbox,
  Info,
  LoaderCircle,
  RefreshCw,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { STATUS_LABEL } from '../lib/format.ts';
import { axisNumber, compactFigure, layoutBars } from '../lib/chart.ts';
import { MSG } from '../content/messages.ts';

const STATUS_TONE: Record<string, string> = {
  APPROVED: 'ok',
  MATCHED: 'ok',
  RESOLVED: 'ok',
  APROVADA: 'ok',
  ENCERRADA: 'muted',
  COMMITTED: 'ok',
  COMPARABLE: 'ok',
  CLOSED: 'info',
  CHECKED: 'info',
  JUSTIFICADA: 'info',
  TOLERABLE_DIFFERENCE: 'info',
  ACKNOWLEDGED: 'info',
  PREVIEW: 'info',
  DRAFT: 'muted',
  NOT_STARTED: 'muted',
  CANCELLED: 'muted',
  DISCARDED: 'muted',
  NO_BASE: 'muted',
  RETURNED: 'warn',
  REOPEN_PENDING: 'warn',
  REOPENED: 'warn',
  PARTIAL: 'warn',
  PENDING_STATEMENT: 'warn',
  PENDING_MOVEMENT: 'warn',
  PENDING_REVIEW: 'warn',
  EM_ANALISE: 'warn',
  NOT_COMPARABLE: 'warn',
  OPEN: 'warn',
  ABERTA: 'warn',
  DIVERGENT: 'danger',
  CRITICA: 'danger',
  ALTA: 'danger',
  MEDIA: 'warn',
  BAIXA: 'muted',
};

/** Status is always written out (label), never conveyed by colour alone. */
export function StatusBadge({ status }: { status: string }) {
  return <span className={`badge ${STATUS_TONE[status] ?? 'muted'}`}>{STATUS_LABEL[status] ?? status}</span>;
}

/** Shown wherever a figure depends on an open business decision (docs/03-decision-register.md). */
export function DemoBadge({ title = MSG.provisional.title }: { title?: string }) {
  return (
    <span className="badge demo" title={title}>
      {MSG.provisional.badge}
    </span>
  );
}

/**
 * Page title in the Anashopping style (slides 2–8): graphite title with the last word highlighted in red.
 * Single-word titles stay graphite. The accessible name is the plain text.
 */
export function PageTitle({ children, id }: { children: string; id?: string }) {
  const words = children.trim().split(/\s+/);
  if (words.length < 2) return <h1 id={id}>{children}</h1>;
  const last = words.pop();
  return (
    <h1 id={id}>
      {words.join(' ')} <span className="accent">{last}</span>
    </h1>
  );
}

/**
 * Keeps an indicator figure inside its card. CSS sizes the text by its length (`--chars`, styles.css); when even the
 * smallest readable size cannot hold it (R$ trillions in a narrow card), the figure switches to compact notation
 * ("R$ 1,2 tri"). The exact value stays in the title and in text read by screen readers.
 */
function useFittedFigure(exact: string | null) {
  const ref = useRef<HTMLDivElement>(null);
  const compactText = useMemo(() => (exact === null ? null : compactFigure(exact)), [exact]);
  // Card width at which the exact figure overflowed; tied to the text, so a new value is measured again.
  const [overflow, setOverflow] = useState<{ text: string; width: number } | null>(null);
  const compact = compactText !== null && overflow !== null && overflow.text === exact;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || exact === null || compactText === null) return;
    const check = () => {
      if (!compact) {
        if (el.scrollWidth > el.clientWidth + 1) setOverflow({ text: exact, width: el.clientWidth });
      } else if (overflow && el.clientWidth > overflow.width) {
        setOverflow(null); // more room now: try the exact figure again
      }
    };
    check();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    // Montserrat may arrive after the first measurement and is wider than the fallback font.
    const fonts = (document as { fonts?: FontFaceSet }).fonts;
    fonts?.addEventListener('loadingdone', check);
    return () => {
      ro?.disconnect();
      fonts?.removeEventListener('loadingdone', check);
    };
  }, [exact, compactText, compact, overflow]);

  return { ref, compact, shown: compact ? compactText : exact };
}

/** KPI card (slide 3). `to` makes it a drill-down link to the records behind the number. */
export function Kpi({
  label,
  value,
  sub,
  testId,
  icon: Icon,
  variant,
  to,
  drillLabel = 'Ver registros',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  testId?: string;
  icon?: LucideIcon;
  variant?: 'brand' | 'small';
  to?: string;
  drillLabel?: string;
}) {
  const exact = typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  const fit = useFittedFigure(exact);
  const body = (
    <>
      <div className="kpi-head">
        {Icon ? (
          <span className="kpi-icon" aria-hidden="true">
            <Icon size={18} strokeWidth={2} />
          </span>
        ) : null}
        <div className="label">{label}</div>
      </div>
      {fit.shown === null ? (
        <div className="value">{value}</div>
      ) : (
        <div
          ref={fit.ref}
          className="value fit"
          style={{ '--chars': fit.shown.length } as CSSProperties}
          title={fit.compact ? (exact ?? undefined) : undefined}
        >
          {fit.compact ? (
            <>
              <span aria-hidden="true">{fit.shown}</span>
              <span className="visually-hidden">{exact}</span>
            </>
          ) : (
            fit.shown
          )}
        </div>
      )}
      {sub ? <div className="sub">{sub}</div> : null}
      {to ? (
        <div className="drill">
          {drillLabel} <ChevronRight size={12} aria-hidden="true" style={{ verticalAlign: '-2px' }} />
        </div>
      ) : null}
    </>
  );
  const cls = `kpi${variant ? ` ${variant}` : ''}`;
  return to ? (
    <Link className={cls} to={to} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <div className={cls} data-testid={testId}>
      {body}
    </div>
  );
}

export function Loading({ what = MSG.common.loading }: { what?: string }) {
  return (
    <p className="loading" role="status" aria-live="polite">
      <LoaderCircle size={16} className="spin" aria-hidden="true" />
      {what}…
    </p>
  );
}

type UpdateWindow = Window & { __anaUpdate?: () => void };

/** Offered when a new version of the app is waiting (service worker in prompt mode). */
export function UpdateBanner() {
  const [apply, setApply] = useState<(() => void) | null>(() => (window as UpdateWindow).__anaUpdate ?? null);
  useEffect(() => {
    const onReady = () => setApply(() => (window as UpdateWindow).__anaUpdate ?? null);
    window.addEventListener('ana:update-ready', onReady);
    return () => window.removeEventListener('ana:update-ready', onReady);
  }, []);
  if (!apply) return null;
  return (
    <div className="update-banner" role="status" data-testid="update-banner">
      <RefreshCw size={18} aria-hidden="true" />
      <span>{MSG.update.available}</span>
      <button className="btn small primary" onClick={apply}>
        {MSG.update.apply}
      </button>
    </div>
  );
}

/** Delay before the page-level loading state shows anything. */
export const SPLASH_DELAY_MS = 300;

/**
 * Page-level loading state while the session is checked (brand symbol; not an app splash screen). Shown only when
 * the wait lasts more than SPLASH_DELAY_MS: a quick check goes straight to the screen, without a flash of the symbol
 * and without spending the phone's bandwidth on the symbol and the web font before the first real content.
 */
export function Splash({ what = MSG.common.checkingSession }: { what?: string }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), SPLASH_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="splash" role="status" aria-live="polite">
      {shown ? (
        <>
          <img src="/brand/anashopping-simbolo-vermelho.png" alt="" width={64} height={58} />
          <span>{what}…</span>
        </>
      ) : null}
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: string; retry?: () => void }) {
  return (
    <div className="alert danger" role="alert">
      <TriangleAlert size={18} aria-hidden="true" />
      <div>
        {error}{' '}
        {retry ? (
          <button className="btn small" onClick={retry}>
            {MSG.common.retry}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function Empty({ children, icon: Icon = Inbox }: { children: ReactNode; icon?: LucideIcon }) {
  return (
    <div className="empty">
      <Icon size={20} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

// ---------- toasts ----------
interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  text: string;
}
const TOAST_ICON = { success: CircleCheck, error: CircleAlert, info: Info } as const;
/** Time on screen: errors stay longer (they may need reading twice). Paused while the pointer or focus is on them. */
export const TOAST_MS = { success: 4000, info: 4000, error: 8000 } as const;
const MAX_TOASTS = 3;
const ToastCtx = createContext<(kind: Toast['kind'], text: string) => void>(() => {});

/**
 * Operation feedback (docs/design-system/ACTION_COMPONENTS.md). Two live regions are always mounted, so screen
 * readers announce what is added: confirmations politely, errors at once (assertive). A message already on screen is
 * not stacked again; at most three are shown. Each can be closed; timers pause on hover or focus (WCAG 2.2.1).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  // Source of truth outside React state, so timers are never started from inside a state updater.
  const itemsRef = useRef<Toast[]>([]);
  const timers = useRef(
    new Map<number, { handle: ReturnType<typeof setTimeout>; due: number; left: number }>(),
  );
  const paused = useRef(false);
  const commit = useCallback((next: Toast[]) => {
    itemsRef.current = next;
    setItems(next);
  }, []);
  const forget = (id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t.handle);
    timers.current.delete(id);
  };
  const remove = useCallback(
    (id: number) => {
      forget(id);
      commit(itemsRef.current.filter((x) => x.id !== id));
    },
    [commit],
  );
  const schedule = useCallback(
    (id: number, ms: number) => {
      const prev = timers.current.get(id);
      if (prev) clearTimeout(prev.handle);
      const entry = { handle: setTimeout(() => remove(id), ms), due: Date.now() + ms, left: ms };
      if (paused.current) clearTimeout(entry.handle);
      timers.current.set(id, entry);
    },
    [remove],
  );
  const push = useCallback(
    (kind: Toast['kind'], text: string) => {
      const list = itemsRef.current;
      const same = list.find((x) => x.kind === kind && x.text === text);
      if (same) {
        schedule(same.id, TOAST_MS[kind]);
        return;
      }
      const id = Date.now() + Math.random();
      schedule(id, TOAST_MS[kind]);
      const next = [...list, { id, kind, text }];
      next.slice(0, Math.max(0, next.length - MAX_TOASTS)).forEach((t) => forget(t.id));
      commit(next.slice(-MAX_TOASTS));
    },
    [schedule, commit],
  );
  useEffect(() => {
    const all = timers.current;
    return () => all.forEach((t) => clearTimeout(t.handle));
  }, []);
  const pause = () => {
    if (paused.current) return;
    paused.current = true;
    for (const t of timers.current.values()) {
      clearTimeout(t.handle);
      t.left = Math.max(1000, t.due - Date.now());
    }
  };
  const resume = () => {
    if (!paused.current) return;
    paused.current = false;
    for (const [id, t] of timers.current) schedule(id, t.left);
  };
  const render = (list: Toast[]) =>
    list.map((t) => {
      const Icon = TOAST_ICON[t.kind];
      return (
        <div key={t.id} className={`toast ${t.kind}`}>
          <Icon size={18} aria-hidden="true" />
          <span>{t.text}</span>
          <button
            type="button"
            className="toast-close"
            aria-label="Fechar aviso"
            onClick={() => remove(t.id)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      );
    });
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        className="toast-host"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onFocus={pause}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) resume();
        }}
      >
        <div aria-live="polite" data-testid="toast-polite">
          {render(items.filter((t) => t.kind !== 'error'))}
        </div>
        <div aria-live="assertive" data-testid="toast-alert">
          {render(items.filter((t) => t.kind === 'error'))}
        </div>
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

export interface BarDatum {
  label: string;
  value: number;
  previous?: number | null;
  /** Provisional (not approved) values are drawn hollow and named in the legend and the data table. */
  provisional?: boolean;
  title?: string;
}

/**
 * Accessible bar chart (SVG). Screen readers get the same figures as a table; colour is never the only cue
 * (provisional bars are hollow, previous-period bars are light with an outline).
 *
 * Laid out in real pixels for the measured width (lib/chart.ts), so labels keep their size on a phone. The value
 * axis is "nice" with headroom and compact labels (`axisFormat`: money charts pass axisBrl, values in centavos).
 */
export function Bars({
  data,
  height = 200,
  label = 'Gráfico de barras',
  format = (n: number) => String(n),
  axisFormat = axisNumber,
  barWidth = 24,
}: {
  data: BarDatum[];
  height?: number;
  label?: string;
  format?: (n: number) => string;
  axisFormat?: (n: number) => string;
  /** Maximum width of one bar, in pixels. */
  barWidth?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const [measured, setMeasured] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      if (w > 0) setMeasured(w);
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // 600 only until the first measurement (and where there is no layout, as in unit tests).
  const width = measured || 600;
  const hasPrev = data.some((d) => d.previous !== undefined && d.previous !== null);
  const { plot, ticks, bars, xLabels } = layoutBars(data, {
    width,
    height,
    maxBarWidth: barWidth,
    axisFormat,
  });
  return (
    <figure ref={ref} className="chart-figure">
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        <rect
          className="plot-area"
          x={plot.left}
          y={plot.top}
          width={plot.right - plot.left}
          height={plot.bottom - plot.top}
        />
        {ticks.map((t) => (
          <g key={t.value}>
            <line
              className={`grid-line${t.value === 0 ? ' zero' : ''}`}
              x1={plot.left}
              x2={plot.right}
              y1={t.y}
              y2={t.y}
            />
            <text className="tick" x={plot.left - 6} y={t.y} dy="0.35em" textAnchor="end">
              {t.label}
            </text>
          </g>
        ))}
        {bars.map((b) => {
          const d = data[b.index]!;
          return b.kind === 'previous' ? (
            <rect key={`p${b.index}`} className="bar prev" x={b.x} y={b.y} width={b.width} height={b.height}>
              <title>{`${d.label} (período anterior): ${format(d.previous ?? 0)}`}</title>
            </rect>
          ) : (
            <rect
              key={`c${b.index}`}
              className={`bar${d.provisional ? ' provisional' : ''}`}
              x={b.x}
              y={b.y}
              width={b.width}
              height={b.height}
            >
              <title>
                {d.title ?? `${d.label}: ${format(d.value)}${d.provisional ? ' (provisório)' : ''}`}
              </title>
            </rect>
          );
        })}
        {xLabels.map((l) => (
          <text key={l.index} x={l.x} y={l.y} textAnchor="middle">
            {l.text}
          </text>
        ))}
      </svg>
      <div className="visually-hidden">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Valor</th>
              {hasPrev ? <th scope="col">Período anterior</th> : null}
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={`${d.label}-${i}`}>
                <td>{d.label}</td>
                <td>
                  {format(d.value)}
                  {d.provisional ? ' (provisório)' : ''}
                </td>
                {hasPrev ? (
                  <td>{d.previous === null || d.previous === undefined ? '—' : format(d.previous)}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/** Horizontal share bars (receitas por meio de pagamento / operadora). */
export function HBars({
  rows,
  format,
  label,
}: {
  rows: { label: string; value: number }[];
  format: (n: number) => string;
  label: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="hbar-box">
      <div className="hbar" role="list" aria-label={label}>
        {rows.map((r) => (
          <div key={r.label} role="listitem" style={{ display: 'contents' }}>
            <span>{r.label}</span>
            <span className="track" aria-hidden="true">
              <span
                className="fill"
                style={{ width: `${Math.min(100, Math.max(0, (r.value / max) * 100))}%`, display: 'block' }}
              />
            </span>
            <span className="val">{format(r.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Workflow progress (slide 10: numbered circles with a dashed connector). */
export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="stepper" aria-label="Etapas do fluxo">
      {steps.map((s, i) => (
        <li
          key={s}
          className={i < current ? 'done' : i === current ? 'current' : ''}
          aria-current={i === current ? 'step' : undefined}
        >
          <span className="n" aria-hidden="true">
            {i < current ? '✓' : i + 1}
          </span>
          {s}
          {i < current ? <span className="visually-hidden"> (concluída)</span> : null}
        </li>
      ))}
    </ol>
  );
}
